import { useEffect, useMemo, useState } from 'react'
import { differenceInHours, format } from 'date-fns'
import { Link, useSearchParams } from 'react-router-dom'
import { cancelBooking, getBookingByToken } from '../api/bookings'
import type { BookingDetails } from '../types/booking'

type ToastState = {
  tone: 'success' | 'error'
  message: string
}

const HOURS_REFUND_CUTOFF = 24

const CancelBooking = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [loading, setLoading] = useState(true)
  const [booking, setBooking] = useState<BookingDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [hasCancelled, setHasCancelled] = useState(false)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const fetchBooking = async () => {
      if (!token) {
        setError('Booking not found. Please verify your link.')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        const response = await getBookingByToken(token)
        if (!isMounted) return
        setBooking(response.booking)

        if (!response.canCancel) {
          const alreadyCancelled = response.booking.status?.toLowerCase() === 'cancelled'
          setEligibilityMessage(
            alreadyCancelled
              ? 'This booking has already been cancelled.'
              : 'This reservation can no longer be cancelled online.',
          )
          if (alreadyCancelled) {
            setHasCancelled(true)
          }
        } else {
          setEligibilityMessage(null)
        }
      } catch (err: unknown) {
        console.error(err)
        if (!isMounted) return

        if (err instanceof Error) {
          setError(err.message || 'Booking not found.')
        } else {
          setError('Booking not found.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchBooking()

    return () => {
      isMounted = false
    }
  }, [token])

  const bookingStart = useMemo(() => {
    if (!booking?.startTime) return null
    const date = new Date(booking.startTime)
    return Number.isNaN(date.getTime()) ? null : date
  }, [booking?.startTime])

  const hoursUntilBooking = useMemo(() => {
    if (!bookingStart) return null
    return differenceInHours(bookingStart, new Date())
  }, [bookingStart])

  const isWithinRefundCutoff = Boolean(
    hoursUntilBooking !== null && hoursUntilBooking < HOURS_REFUND_CUTOFF,
  )

  const handleCancel = async () => {
    if (!booking || !token) return

    try {
      setIsCancelling(true)
      setToast(null)
      await cancelBooking(booking.id, token)
      setHasCancelled(true)
      setToast({
        tone: 'success',
        message: 'Your booking has been cancelled. A confirmation email will arrive shortly.',
      })
    } catch (err: unknown) {
      console.error(err)
      setToast({
        tone: 'error',
        message:
          err instanceof Error ? err.message : 'We could not cancel your booking at this time. Please try again.',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-6">
          <div className="h-4 w-40 animate-pulse rounded-full bg-widget-primary/20" />
          <div className="h-24 animate-pulse rounded-3xl bg-widget-primary/10" />
          <div className="h-32 animate-pulse rounded-3xl bg-widget-primary/10" />
        </div>
      )
    }

    if (error) {
      return (
        <div role="alert" className="superz-widget-panel p-6 text-center text-sm text-red-200">
          {error}
        </div>
      )
    }

    if (!booking) {
      return (
        <div role="alert" className="superz-widget-panel p-6 text-center text-sm text-red-200">
          Booking not found.
        </div>
      )
    }

    return (
      <>
        <section className="superz-widget-panel space-y-4 p-6">
          <header className="space-y-2">
            <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Booking Overview</p>
            <h1 className="font-display text-3xl text-widget-accent">Cancel your experience</h1>
          </header>

          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="rounded-2xl border border-widget-primary/30 bg-widget-primary/10 p-4">
              <dt className="text-xs uppercase tracking-[0.3em] text-widget-accent/60">Guest</dt>
              <dd className="mt-1 text-lg font-semibold text-widget-accent">{booking.customerName}</dd>
              <dd className="text-xs text-widget-accent/60">{booking.customerEmail ?? 'Email on file'}</dd>
            </div>
            <div className="rounded-2xl border border-widget-primary/30 bg-widget-primary/10 p-4">
              <dt className="text-xs uppercase tracking-[0.3em] text-widget-accent/60">Reservation</dt>
              <dd className="mt-1 text-lg font-semibold text-widget-accent">
                {bookingStart ? format(bookingStart, 'EEEE, MMMM do') : 'Date unavailable'}
              </dd>
              <dd className="text-xs text-widget-accent/60">
                {bookingStart ? format(bookingStart, 'HH:mm') : 'Time unavailable'} ·{' '}
                {booking.guestCount} guest{booking.guestCount !== 1 && 's'}
              </dd>
            </div>
          </dl>

          <div
            className={`rounded-3xl border px-6 py-5 text-sm ${
              isWithinRefundCutoff
                ? 'border-red-500/30 bg-red-500/10 text-red-100'
                : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
            }`}
          >
            {isWithinRefundCutoff ? (
              <>
                <p className="font-semibold">Too late to cancel</p>
                <p className="mt-1 text-xs opacity-80">
                  We are within 24 hours of your reservation. Online cancellations and refunds are no longer available.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">Full refund available</p>
                <p className="mt-1 text-xs opacity-80">
                  Cancelling at least 24 hours in advance ensures your refund is processed in full.
                </p>
              </>
            )}
          </div>
        </section>

        <section className="superz-widget-panel space-y-6 p-6">
          <div className="space-y-3 text-sm text-widget-accent/80">
            <p>
              By cancelling, you acknowledge that the reservation will be released immediately. If you would prefer to
              move your visit, you may explore new dates via the reschedule option instead.
            </p>
            {!hasCancelled && (
              <p>
                Need to keep your plans flexible?{' '}
                <Link
                  to={`/reschedule?token=${encodeURIComponent(token)}`}
                  className="underline decoration-widget-secondary decoration-2 underline-offset-4 hover:text-widget-secondary"
                >
                  Reschedule your experience
                </Link>
                .
              </p>
            )}
          </div>

          {eligibilityMessage && (
            <p role="status" className="rounded-2xl border border-widget-primary/30 bg-widget-primary/10 p-4 text-sm text-widget-accent/80">
              {eligibilityMessage}
            </p>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isCancelling || hasCancelled || isWithinRefundCutoff || Boolean(eligibilityMessage && !hasCancelled)}
              className="inline-flex items-center justify-center rounded-full border border-widget-secondary bg-widget-secondary px-8 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-widget-primary transition-all duration-200 shadow-[0_4px_12px_rgba(var(--widget-secondary-rgb),0.3)] hover:shadow-[0_8px_20px_rgba(var(--widget-secondary-rgb),0.4)] hover:scale-102 active:scale-98 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary disabled:cursor-not-allowed disabled:border-widget-accent/20 disabled:bg-widget-accent/10 disabled:text-widget-accent/40 disabled:shadow-none"
              aria-disabled={isCancelling || hasCancelled || isWithinRefundCutoff || Boolean(eligibilityMessage && !hasCancelled)}
            >
              {isCancelling ? 'Cancelling…' : hasCancelled ? 'Booking Cancelled' : 'Confirm Cancellation'}
            </button>

            <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/50">
              {isWithinRefundCutoff ? 'Cancellation window closed' : 'Refund guaranteed'}
            </p>
          </div>
        </section>
      </>
    )
  }

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-10 text-widget-accent">
      {toast && (
        <div
          role="status"
          aria-live="assertive"
          className={`sticky top-6 z-20 rounded-full px-5 py-3 text-sm shadow-lg backdrop-blur ${
            toast.tone === 'success'
              ? 'bg-emerald-500/20 text-emerald-100'
              : 'bg-red-500/20 text-red-100'
          }`}
        >
          {toast.message}
        </div>
      )}

      {renderContent()}
    </main>
  )
}

export default CancelBooking


